import { IAxiosReturn } from "functions/api/interface";
import { compressBundleFile } from "./compressBundleFile";
import { hashBundle } from "./hashBundle";
import {
  TLF_BUNDLE_UPLOAD_BATCH_SIZE,
  TlfBundleImportProgress,
  TlfBundleUploadAnalysis,
} from "./interface";
import { splitBundleChunks } from "./splitBundleChunks";
import { tlfBundleApi } from "./tlfBundleApi";

/**
 * File and target hierarchy used by the upload-and-analysis workflow.
 */
export interface UploadTlfBundleTarget {
  hierarchyId: string;
  file: File;
}

/**
 * Compress, upload sequential chunks, complete the upload, and analyse it.
 *
 * @param backEndUrl - Backend base URL
 * @param target - Target hierarchy and selected bundle file
 * @param controller - Controller used to cancel the active transfer request
 * @param onProgress - Optional callback for transfer and analysis progress
 * @returns Completed upload metadata and the target hierarchy analysis
 */
export const uploadAndAnalyzeTlfBundle = async (
  backEndUrl: string,
  target: UploadTlfBundleTarget,
  controller: AbortController,
  onProgress?: (progress: TlfBundleImportProgress) => void,
): Promise<IAxiosReturn<TlfBundleUploadAnalysis>> => {
  try {
    onProgress?.({
      stage: "compressing",
      completedChunks: 0,
      totalChunks: 0,
      transferredBytes: 0,
      totalBytes: target.file.size,
      currentChunkPercent: 0,
    });
    const bundle = await compressBundleFile(target.file);
    if (controller.signal.aborted) {
      return { success: false, errorText: "Upload TLF bundle failed, request cancelled" };
    }

    const chunks = splitBundleChunks(bundle);
    if (chunks.length === 0) {
      return { success: false, errorText: "The selected bundle is empty" };
    }

    const uploadId = crypto.randomUUID();
    const sha256 = await hashBundle(bundle);
    if (controller.signal.aborted) {
      return { success: false, errorText: "Upload TLF bundle failed, request cancelled" };
    }

    let transferredBytes = 0;
    onProgress?.({
      stage: "uploading",
      completedChunks: 0,
      totalChunks: chunks.length,
      transferredBytes,
      totalBytes: bundle.size,
      currentChunkPercent: 0,
    });

    for (
      let batchStart = 0;
      batchStart < chunks.length;
      batchStart += TLF_BUNDLE_UPLOAD_BATCH_SIZE
    ) {
      if (controller.signal.aborted) {
        return { success: false, errorText: "Upload TLF bundle failed, request cancelled" };
      }
      const batch = chunks.slice(batchStart, batchStart + TLF_BUNDLE_UPLOAD_BATCH_SIZE);
      const progressByChunk = new Map<number, number>();
      const batchResponses = await Promise.all(
        batch.map((chunk, batchIndex) => {
          const sequenceNumber = batchStart + batchIndex;
          return tlfBundleApi.uploadChunk(
            backEndUrl,
            {
              hierarchyId: target.hierarchyId,
              uploadId,
              sequenceNumber,
              totalChunks: chunks.length,
              chunk,
            },
            controller,
            (currentChunkPercent) => {
              progressByChunk.set(sequenceNumber, currentChunkPercent);
              const batchTransferredBytes = batch.reduce(
                (total, batchChunk, index) =>
                  total +
                  Math.round(
                    (batchChunk.size * (progressByChunk.get(batchStart + index) ?? 0)) / 100,
                  ),
                0,
              );
              const averageChunkPercent = Math.round(
                [...progressByChunk.values()].reduce((total, percent) => total + percent, 0) /
                  batch.length,
              );
              onProgress?.({
                stage: "uploading",
                completedChunks: batchStart,
                totalChunks: chunks.length,
                transferredBytes: transferredBytes + batchTransferredBytes,
                totalBytes: bundle.size,
                currentChunkPercent: averageChunkPercent,
              });
            },
          );
        }),
      );
      const failedResponse = batchResponses.find((response) => !response.success);
      if (failedResponse) return failedResponse;

      transferredBytes += batch.reduce((total, chunk) => total + chunk.size, 0);
      onProgress?.({
        stage: "uploading",
        completedChunks: batchStart + batch.length,
        totalChunks: chunks.length,
        transferredBytes,
        totalBytes: bundle.size,
        currentChunkPercent: 100,
      });
    }

    if (controller.signal.aborted) {
      return { success: false, errorText: "Upload TLF bundle failed, request cancelled" };
    }
    onProgress?.({
      stage: "completing",
      completedChunks: chunks.length,
      totalChunks: chunks.length,
      transferredBytes: bundle.size,
      totalBytes: bundle.size,
      currentChunkPercent: 100,
    });
    const completeResponse = await tlfBundleApi.completeUpload(
      backEndUrl,
      { hierarchyId: target.hierarchyId, uploadId, totalBytes: bundle.size, sha256 },
      controller,
    );
    if (!completeResponse.success) return completeResponse;
    if (controller.signal.aborted) {
      return { success: false, errorText: "Upload TLF bundle failed, request cancelled" };
    }

    onProgress?.({
      stage: "analysing",
      completedChunks: chunks.length,
      totalChunks: chunks.length,
      transferredBytes: bundle.size,
      totalBytes: bundle.size,
      currentChunkPercent: 100,
    });
    const analysisResponse = await tlfBundleApi.analyzeUpload(
      backEndUrl,
      { hierarchyId: target.hierarchyId, uploadId: completeResponse.data.uploadId },
      controller,
    );
    if (!analysisResponse.success) return analysisResponse;

    return {
      success: true,
      data: {
        uploadId: completeResponse.data.uploadId,
        compressedBytes: bundle.size,
        analysis: analysisResponse.data,
      },
    };
  } catch (error: unknown) {
    return {
      success: false,
      errorText:
        error instanceof Error
          ? `Upload TLF bundle failed, ${error.message}`
          : "Upload TLF bundle failed",
    };
  }
};
